const moment = require("moment");
const { weekDays, weekStart, shiftDay } = require("../mobile/dayNav.js");

const CALENDAR_DAY_RE = /^\d{8}$/;

function isCalendarDayId(id) {
  return typeof id === "string" && CALENDAR_DAY_RE.test(id);
}

function normalizeDayId(value) {
  if (isCalendarDayId(value)) return value;
  if (!value) return null;
  const m = moment(value);
  if (!m.isValid()) return null;
  return m.format("YYYYMMDD");
}

function daysInclusive(start, end) {
  const startId = normalizeDayId(start);
  const endId = normalizeDayId(end);
  if (!startId || !endId) return [];
  let from = startId;
  let to = endId;
  if (from > to) {
    const tmp = from;
    from = to;
    to = tmp;
  }
  const days = [];
  let cursor = from;
  while (cursor <= to) {
    days.push(cursor);
    cursor = shiftDay(cursor, 1);
  }
  return days;
}

function presetRange(kind, now = new Date(), weekStartsOnMonday = true) {
  const m = moment(now);
  if (kind === "month") {
    return {
      start: m.clone().startOf("month").format("YYYYMMDD"),
      end: m.clone().endOf("month").format("YYYYMMDD"),
    };
  }
  if (kind === "year") {
    return {
      start: m.clone().startOf("year").format("YYYYMMDD"),
      end: m.clone().endOf("year").format("YYYYMMDD"),
    };
  }
  // week (default)
  const day = m.format("YYYYMMDD");
  const days = weekDays(day, weekStartsOnMonday);
  return { start: days[0], end: days[days.length - 1] };
}

function todoCalendarDate(todo) {
  if (!todo || typeof todo !== "object") return null;
  const candidates = [todo.date, todo.calendarDate, todo.dayId];
  for (let i = 0; i < candidates.length; i++) {
    const id = normalizeDayId(candidates[i]);
    if (id) return id;
  }
  if (isCalendarDayId(todo.listId)) return todo.listId;
  return null;
}

function buildDateRangeExport({
  start,
  end,
  todoListsById = {},
  customListIds = [],
  exportedAt = new Date().toISOString(),
} = {}) {
  const startId = normalizeDayId(start);
  const endId = normalizeDayId(end);
  if (!startId || !endId) {
    throw new Error("Invalid date range");
  }
  const rangeStart = startId <= endId ? startId : endId;
  const rangeEnd = startId <= endId ? endId : startId;
  const daySet = new Set(daysInclusive(rangeStart, rangeEnd));

  const calendar = {};
  daySet.forEach((dayId) => {
    const list = todoListsById[dayId];
    if (Array.isArray(list)) {
      calendar[dayId] = list;
    }
  });

  const customLists = (customListIds || [])
    .map((entry) => {
      const listId = entry && entry.listId != null ? entry.listId : entry;
      const listName = entry && entry.listName != null ? entry.listName : String(listId);
      const todos = Array.isArray(todoListsById[listId]) ? todoListsById[listId] : [];
      const filtered = todos.filter((todo) => {
        const day = todoCalendarDate(todo);
        return day && daySet.has(day);
      });
      if (!filtered.length) return null;
      return { listId, listName, todos: filtered };
    })
    .filter(Boolean);

  return {
    type: "weektodo-date-range-export",
    version: 1,
    exportedAt,
    range: { start: rangeStart, end: rangeEnd },
    calendar,
    customLists,
  };
}

function downloadJsonFile(filename, data) {
  const body = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  const element = document.createElement("a");
  element.setAttribute("href", "data:application/json;charset=utf-8," + encodeURIComponent(body));
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

async function loadCalendarTodoListsForRange({
  start,
  end,
  weekStartsOnMonday = true,
  fetchRange,
} = {}) {
  const startId = normalizeDayId(start);
  const endId = normalizeDayId(end);
  if (!startId || !endId) {
    throw new Error("Invalid date range");
  }
  const rangeStart = startId <= endId ? startId : endId;
  const rangeEnd = startId <= endId ? endId : startId;
  if (typeof fetchRange !== "function") {
    throw new Error("fetchRange is required");
  }

  const todoListsById = {};
  let weekFrom = weekStart(rangeStart, weekStartsOnMonday);
  while (weekFrom <= rangeEnd) {
    const weekEnd = shiftDay(weekFrom, 6);
    const { documents } = await fetchRange(weekFrom, weekEnd);
    for (const doc of documents || []) {
      if (!doc || doc.docId == null) continue;
      todoListsById[String(doc.docId)] = Array.isArray(doc.payload) ? doc.payload : [];
    }
    weekFrom = shiftDay(weekFrom, 7);
  }
  return todoListsById;
}

async function loadCustomTodoListsForExport({ customListIds = [], getDocument } = {}) {
  const todoListsById = {};
  if (typeof getDocument !== "function") return todoListsById;
  for (const entry of customListIds || []) {
    const listId = entry && entry.listId != null ? entry.listId : entry;
    if (listId == null || isCalendarDayId(String(listId))) continue;
    const id = String(listId);
    const doc = await getDocument("todo_list", id);
    if (doc) {
      todoListsById[id] = Array.isArray(doc.payload) ? doc.payload : [];
    }
  }
  return todoListsById;
}

async function loadTodoListsForDateRangeExport({
  start,
  end,
  weekStartsOnMonday = true,
  customListIds = [],
  fetchRange,
  getDocument,
} = {}) {
  const calendar = await loadCalendarTodoListsForRange({
    start,
    end,
    weekStartsOnMonday,
    fetchRange,
  });
  const custom = await loadCustomTodoListsForExport({ customListIds, getDocument });
  return { ...calendar, ...custom };
}

function loadAllTodoListsFromIdb(dbRepository) {
  return new Promise((resolve, reject) => {
    const req = dbRepository.open();
    req.onerror = () => reject(req.error || new Error("Failed to open database"));
    req.onsuccess = (event) => {
      const db = event.target.result;
      const todoListsById = {};
      const cursorReq = dbRepository.selectAll(db, "todo_lists");
      cursorReq.onerror = () => reject(cursorReq.error || new Error("Failed to read todo_lists"));
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          todoListsById[cursor.key] = cursor.value;
          cursor.continue();
          return;
        }
        resolve(todoListsById);
      };
    };
  });
}

async function exportDateRangeToFile({
  start,
  end,
  customListIds,
  weekStartsOnMonday = true,
  fetchRange,
  getDocument,
  dbRepository,
  exportedAt,
} = {}) {
  let todoListsById;
  if (typeof fetchRange === "function") {
    todoListsById = await loadTodoListsForDateRangeExport({
      start,
      end,
      weekStartsOnMonday,
      customListIds,
      fetchRange,
      getDocument,
    });
  } else {
    todoListsById = await loadAllTodoListsFromIdb(dbRepository);
  }
  const payload = buildDateRangeExport({
    start,
    end,
    todoListsById,
    customListIds,
    exportedAt,
  });
  const filename = `weektodo-${payload.range.start}-${payload.range.end}.json`;
  downloadJsonFile(filename, payload);
  return payload;
}

module.exports = {
  isCalendarDayId,
  normalizeDayId,
  daysInclusive,
  presetRange,
  todoCalendarDate,
  buildDateRangeExport,
  downloadJsonFile,
  loadCalendarTodoListsForRange,
  loadCustomTodoListsForExport,
  loadTodoListsForDateRangeExport,
  loadAllTodoListsFromIdb,
  exportDateRangeToFile,
};
